import {useMemo, type ReactNode} from "react";

import {cn} from "@/lib/utils";

import {getResidentHomePillarVisibility} from "../lib/resident-home-pillar-visibility";

import {ResidentHomeAmenitiesCard} from "./resident-home-amenities-card";
import {ResidentHomeCitationsCard} from "./resident-home-citations-card";
import {ResidentHomePermanentPermitsCard} from "./resident-home-permanent-permits-card";
import {ResidentHomeSafelistingsCard} from "./resident-home-safelistings-card";

function getPillarColumnClass(visibleCount: number) {
  return cn(
    "flex h-full min-h-0 min-w-0 flex-col",
    visibleCount === 1 ? "sm:col-span-2 xl:col-span-12" : "sm:col-span-1 xl:col-span-6",
  );
}

export function ResidentHomePillars() {
  const visibility = getResidentHomePillarVisibility();

  const pillars = useMemo(() => {
    const items: Array<{key: string; node: ReactNode}> = [];

    if (visibility.permanentPermits) {
      items.push({
        key: "permanent-permits",
        node: <ResidentHomePermanentPermitsCard className="h-full" />,
      });
    }

    if (visibility.safelistings) {
      items.push({
        key: "safelistings",
        node: <ResidentHomeSafelistingsCard className="h-full" />,
      });
    }

    if (visibility.citations) {
      items.push({
        key: "citations",
        node: <ResidentHomeCitationsCard className="h-full" />,
      });
    }

    if (visibility.amenities) {
      items.push({
        key: "amenities",
        node: <ResidentHomeAmenitiesCard className="h-full" />,
      });
    }

    return items;
  }, [visibility.amenities, visibility.citations, visibility.permanentPermits, visibility.safelistings]);

  if (pillars.length === 0) {
    return null;
  }

  const columnClass = getPillarColumnClass(pillars.length);

  return (
    <section className="flex flex-col gap-3">
      <div className="grid w-full auto-rows-fr grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-12">
        {pillars.map((pillar) => (
          <div key={pillar.key} className={columnClass}>
            <div className="h-full min-h-0 w-full">{pillar.node}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
